/**
 * The small formatting subset event descriptions support, stored as markdown:
 * paragraphs, **bold**, _italic_, [links](https://…) and "- " bullet lists.
 * Both the editor (TipTap ⇄ markdown) and the fan page (markdown → blocks)
 * go through here so what is written is what is shown.
 */
export type Inline = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  href?: string;
};
export type Block =
  | { type: "paragraph"; inlines: Inline[] }
  | { type: "bullets"; items: Inline[][] };

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

type Style = { bold?: boolean; italic?: boolean; href?: string };
const push = (out: Inline[], text: string, style: Style) => {
  if (!text) return;
  const last = out[out.length - 1];
  if (
    last &&
    last.bold === style.bold &&
    last.italic === style.italic &&
    last.href === style.href
  )
    last.text += text;
  else out.push({ text, ...style });
};
/** Find the matching closer for a mark opened at `from`, honouring escapes. */
const findClose = (source: string, marker: string, from: number) => {
  for (let i = from; i < source.length; i += 1) {
    if (source[i] === "\\") {
      i += 1;
      continue;
    }
    if (source.startsWith(marker, i)) return i;
  }
  return -1;
};
function scan(source: string, style: Style, out: Inline[]) {
  let text = "";
  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i]!;
    if (ch === "\\" && i + 1 < source.length) {
      text += source[i + 1];
      i += 1;
      continue;
    }
    if (!style.href && ch === "[") {
      const closeText = findClose(source, "](", i + 1);
      const closeUrl = closeText >= 0 ? source.indexOf(")", closeText + 2) : -1;
      if (closeText > i && closeUrl > closeText) {
        const href = source.slice(closeText + 2, closeUrl);
        push(out, text, style);
        text = "";
        scan(
          source.slice(i + 1, closeText),
          SAFE_HREF.test(href) ? { ...style, href } : style,
          out,
        );
        i = closeUrl;
        continue;
      }
    }
    const marker = source.startsWith("**", i) ? "**" : ch === "_" ? "_" : null;
    if (marker && !(marker === "**" ? style.bold : style.italic)) {
      const close = findClose(source, marker, i + marker.length);
      if (close > i + marker.length) {
        push(out, text, style);
        text = "";
        scan(
          source.slice(i + marker.length, close),
          marker === "**"
            ? { ...style, bold: true }
            : { ...style, italic: true },
          out,
        );
        i = close + marker.length - 1;
        continue;
      }
    }
    text += ch;
  }
  push(out, text, style);
}
export function parseInlines(source: string): Inline[] {
  const out: Inline[] = [];
  scan(source, {}, out);
  return out;
}

export function markdownToBlocks(markdown: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let bullets: string[] | null = null;
  const flush = () => {
    if (paragraph.length) {
      blocks.push({
        type: "paragraph",
        inlines: parseInlines(paragraph.join("\n")),
      });
      paragraph = [];
    }
    if (bullets) {
      blocks.push({ type: "bullets", items: bullets.map(parseInlines) });
      bullets = null;
    }
  };
  for (const raw of markdown.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trimEnd();
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      if (paragraph.length) flush();
      (bullets ??= []).push(bullet[1] ?? "");
    } else if (!line.trim()) flush();
    else {
      if (bullets) flush();
      paragraph.push(line);
    }
  }
  flush();
  return blocks;
}

const escapeText = (text: string) => text.replace(/([\\*_[\]])/g, "\\$1");
export function inlinesToMarkdown(inlines: Inline[]) {
  return inlines
    .map((run) => {
      let text = escapeText(run.text);
      if (run.bold) text = `**${text}**`;
      if (run.italic) text = `_${text}_`;
      if (run.href && SAFE_HREF.test(run.href)) text = `[${text}](${run.href})`;
      return text;
    })
    .join("");
}
export function blocksToMarkdown(blocks: Block[]) {
  return blocks
    .map((block) =>
      block.type === "paragraph"
        ? inlinesToMarkdown(block.inlines)
        : block.items.map((item) => `- ${inlinesToMarkdown(item)}`).join("\n"),
    )
    .join("\n\n")
    .trim();
}

/** Plain text, for previews and meta descriptions. */
export function markdownToPlainText(markdown: string) {
  return markdownToBlocks(markdown)
    .map((block) =>
      block.type === "paragraph"
        ? block.inlines.map((i) => i.text).join("")
        : block.items
            .map((item) => item.map((i) => i.text).join(""))
            .join("\n"),
    )
    .join("\n\n");
}
